import { useContext, useEffect, useState, useCallback, useMemo } from "react";
import { DatePicker, Table, Row, Col, Tabs, Card, Spin } from "antd";
import moment from "moment";
import { Context } from "../../context";
import { Fetch } from "../../fetch";
import { handleExportClick } from "../../utils";
import ExportButton from "../ExportButton";

const { Column } = Table;
const dateFormat = "YYYY-MM-DD";
const { TabPane } = Tabs;

// Filter Components
const DateRangeFilter = ({ value, onChange }) => (
  <Col span={6}>
    <DatePicker.RangePicker
      style={{ width: "100%" }}
      value={value}
      onChange={onChange}
      format={dateFormat}
      allowClear={true}
      placeholder={['Start Date', 'End Date']}
    />
  </Col>
);

const RevenueReport = ({ data, loading, filters, onFiltersChange, apiUrl, token }) => {
  const handleDateChange = (value, dateString) => {
    // Handle cancel/clear case - set to default date range
    if (!value || !dateString || dateString.length === 0) {
      onFiltersChange({
        ...filters,
        date: [moment().startOf("month"), moment().endOf("month")],
      });
      return;
    }

    // Validate dates before setting
    const startDate = moment(dateString[0], dateFormat);
    const endDate = moment(dateString[1], dateFormat);

    if (startDate.isValid() && endDate.isValid()) {
      onFiltersChange({
        ...filters,
        date: [startDate, endDate],
      });
    }
  };

  return (
    <Card>
      <Row style={{ marginBottom: 20 }} align="middle">
        <DateRangeFilter
          value={
            filters.date && filters.date[0] && filters.date[1]
              ? [moment(filters.date[0], dateFormat), moment(filters.date[1], dateFormat)]
              : []
          }
          onChange={handleDateChange}
        />
      </Row>

      {loading ? (
        <div style={{ textAlign: "center", padding: 50 }}>
          <Spin />
        </div>
      ) : data.length === 0 ? (
        <div style={{ textAlign: "center", padding: 50 }}>
          No data available
        </div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <Table
              dataSource={[data]}
              loading={false}
              pagination={false}
              scroll={{ x: 600 }}
              size="small"
            >
              <Column
                title="Subscriptions"
                dataIndex="purchase_count"
                key="purchase_count"
                align="center"
                width={100}
                ellipsis
                render={(value) => value || 0}
              />
              <Column
                title="Subscription Revenue"
                dataIndex="purchase_revenue"
                key="purchase_revenue"
                align="center"
                width={200}
                ellipsis
                render={(value) => value ? `${filters.currency} ${value.toLocaleString()}` : `${filters.currency} 0`}
              />
              <Column
                title="Renewals"
                dataIndex="renewal_count"
                key="renewal_count"
                align="center"
                width={100}
                ellipsis
                render={(value) => value || 0}
              />
              <Column
                title="Renewal Revenue"
                dataIndex="renewal_revenue"
                key="renewal_revenue"
                align="center"
                width={200}
                ellipsis
                render={(value) => value ? `${filters.currency} ${value.toLocaleString()}` : `${filters.currency} 0`}
              />
            </Table>
          </div>

          {/* Summary below table */}
          <Row
            style={{
              marginTop: 16,
              textAlign: "start",
              display: "flex",
              flexDirection: "column",
              justifyContent: "start",
              rowGap: "10px"
            }}
          >
            <div>Total Subscriptions: {data.total_subscriptions.toLocaleString()}</div>
            <div>Total Revenue: INR {data.total_revenue.toLocaleString()}</div>
          </Row>

          <ExportButton
            className="export-btn-only"
            onExport={() => {
              const startDate = moment(filters.date[0]).format(dateFormat);
              const endDate = moment(filters.date[1]).format(dateFormat);
              return handleExportClick(
                apiUrl,
                token,
                `revenue_range?start_date=${startDate}&end_date=${endDate}&currency=${filters.currency}`,
                `revenue_range_${startDate}_${endDate}.csv`
              );
            }}
          />
        </>
      )}
    </Card>
  );
};

const SubscriptionMetricsReport = ({ rawData, loading, filters, onFiltersChange, apiUrl, token }) => {
  const [data, setData] = useState([]);

  // Map backend object to array for Table
  useEffect(() => {
    if (rawData) {
      const mappedData = Object.entries(rawData).map(([key, value]) => ({
        platform: key.replace("_subscriptions", "").toUpperCase(),
        new_subscriptions: value.count,
        percentage: value.percentage,
      }));
      setData(mappedData);
    } else {
      setData([]);
    }
  }, [rawData]);

  // Handle date changes
  const handleDateChange = (value, dateString) => {
    // Handle cancel/clear case - set to default date range
    if (!value || !dateString || dateString.length === 0) {
      onFiltersChange({
        ...filters,
        date: [moment().startOf("month"), moment().endOf("month")],
      });
      return;
    }

    // Validate dates before setting
    const startDate = moment(dateString[0], dateFormat);
    const endDate = moment(dateString[1], dateFormat);

    if (startDate.isValid() && endDate.isValid()) {
      onFiltersChange({
        ...filters,
        date: [startDate, endDate],
      });
    }
  };

  return (
    <Card>
      <Row style={{ marginBottom: 20 }} align="middle">
        <DateRangeFilter
          value={
            filters.date && filters.date[0] && filters.date[1]
              ? [moment(filters.date[0], dateFormat), moment(filters.date[1], dateFormat)]
              : []
          }
          onChange={handleDateChange}
          allowClear={true}
        />
      </Row>

      <div>
        <Table
          dataSource={data}
          rowKey="platform"
          loading={loading}
          size="small"
          pagination={false}
        >
          <Column
            title="Platform"
            dataIndex="platform"
            key="platform"
            align="center"
            width={150}
          />
          <Column
            title="Subscriptions"
            dataIndex="new_subscriptions"
            key="new_subscriptions"
            align="center"
            width={150}
            render={(value) => value || 0}
          />
          <Column
            title="Percentage of Total"
            dataIndex="percentage"
            key="percentage"
            align="center"
            width={150}
            render={(value) => (value !== undefined ? `${value}%` : "0%")}
          />
        </Table>
      </div>

      <ExportButton
        className="export-btn-only"
        onExport={() => {
          const startDate = moment(filters.date[0]).format(dateFormat);
          const endDate = moment(filters.date[1]).format(dateFormat);
          return handleExportClick(
            apiUrl,
            token,
            `subscription_metrics_range?start_date=${startDate}&end_date=${endDate}`,
            `subscription_metrics_${startDate}_${endDate}.csv`
          );
        }}
      />
    </Card>
  );
};

const UserAnalyticsReports = () => {
  const { token, apiUrl } = useContext(Context);

  const [activeTab, setActiveTab] = useState("1");

  // Separate loading states for each report type
  const [revenueLoading, setRevenueLoading] = useState(false);
  const [metricsLoading, setMetricsLoading] = useState(false);

  // Separate state for each report type
  const [revenueData, setRevenueData] = useState([]);
  const [metricsData, setMetricsData] = useState([]);

  // Separate filters for each report type
  const [revenueFilters, setRevenueFilters] = useState({
    date: [moment().startOf("month"), moment().endOf("month")],
    currency: "INR",
  });

  const [metricsFilters, setMetricsFilters] = useState({
    date: [moment().startOf("month"), moment().endOf("month")],
  });

  // Memoized date range keys for comparison
  const revenueDateKey = useMemo(() => {
    if (!revenueFilters.date || !revenueFilters.date[0] || !revenueFilters.date[1]) return null;
    return `${moment(revenueFilters.date[0]).format(dateFormat)}_${moment(revenueFilters.date[1]).format(dateFormat)}`;
  }, [revenueFilters.date]);

  const metricsDateKey = useMemo(() => {
    if (!metricsFilters.date || !metricsFilters.date[0] || !metricsFilters.date[1]) return null;
    return `${moment(metricsFilters.date[0]).format(dateFormat)}_${moment(metricsFilters.date[1]).format(dateFormat)}`;
  }, [metricsFilters.date]);

  // Data fetching functions with proper memoization
  const fetchRevenueData = useCallback(async () => {
    if (!revenueDateKey) return;

    const [startDate, endDate] = revenueDateKey.split('_');

    setRevenueLoading(true);
    Fetch(
      `${apiUrl}/reports/revenue_range?start_date=${startDate}&end_date=${endDate}`,
      token,
      (result) => {
        // Transform the data to match the expected revenue format
        const revenueData = result.data ? {
          purchase_count: result.data.purchase_count || 0,
          renewal_count: result.data.renewal_count || 0,
          purchase_revenue: result.data.purchase_revenue || 0,
          renewal_revenue: result.data.renewal_revenue || 0,
          total_revenue: result.data.total_revenue || 0,
          total_subscriptions: result.data.total_subscriptions || 0
        } : {};
        setRevenueData(revenueData);
        setRevenueLoading(false);
      },
      (error) => {
        console.error('Error fetching revenue data:', error);
        setRevenueLoading(false);
      }
    );
  }, [apiUrl, token, revenueDateKey]);

  const fetchMetricsData = useCallback(async () => {
    if (!metricsDateKey) return;

    const [startDate, endDate] = metricsDateKey.split('_');

    setMetricsLoading(true);
    Fetch(
      `${apiUrl}/reports/subscription_metrics_range?start_date=${startDate}&end_date=${endDate}`,
      token,
      (result) => {
        setMetricsData(result.data);
        setMetricsLoading(false);
      },
      (error) => {
        console.error('Error fetching metrics data:', error);
        setMetricsLoading(false);
      }
    );
  }, [apiUrl, token, metricsDateKey]);

  // Initialize data on component mount
  useEffect(() => {
    fetchRevenueData();
    fetchMetricsData();
  }, [fetchRevenueData, fetchMetricsData]);

  // Handle tab changes to fetch data for the active tab
  const handleTabChange = (key) => {
    setActiveTab(key);

    // Fetch data for the newly active tab if not already loaded
    if (key === "1" && Object.keys(revenueData).length === 0) {
      fetchRevenueData();
    } else if (key === "2" && Object.keys(metricsData).length === 0) {
      fetchMetricsData();
    }
  };

  return (
    <div className="manage-orders-wrapper">
      <Tabs
        defaultActiveKey="1"
        activeKey={activeTab}
        onChange={handleTabChange}
      >
        <TabPane tab="Revenue Reports" key="1">
          <RevenueReport
            data={revenueData}
            loading={revenueLoading}
            filters={revenueFilters}
            onFiltersChange={setRevenueFilters}
            apiUrl={apiUrl}
            token={token}
          />
        </TabPane>

        <TabPane tab="Subscription Metrics" key="2">
          <SubscriptionMetricsReport
            rawData={metricsData}
            loading={metricsLoading}
            filters={metricsFilters}
            onFiltersChange={setMetricsFilters}
            apiUrl={apiUrl}
            token={token}
          />
        </TabPane>
      </Tabs>
    </div>
  );
};

export default UserAnalyticsReports;
